import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useBranchTokenValidation } from '@/hooks/useBranchForm';
import { AlertCircle, Loader2, ShieldX } from 'lucide-react';
import { useParams } from 'react-router-dom';

interface BranchTokenValidationGuardProps {
  children: React.ReactNode;
}

/**
 * Branch Token Validation Guard
 * Validates Branch tokens before allowing access to Branch forms
 * Uses the Branch-specific validation endpoint
 */
export function BranchTokenValidationGuard({
  children,
}: BranchTokenValidationGuardProps) {
  const { token } = useParams<{ token: string }>();

  const {
    data: tokenValidation,
    isLoading,
    error,
    refetch,
  } = useBranchTokenValidation(token || '');

  // Loading state
  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center p-4'>
        <Card className='w-full max-w-md'>
          <CardContent className='flex flex-col items-center justify-center py-8'>
            <Loader2 className='h-8 w-8 animate-spin text-primary mb-4' />
            <p className='text-sm text-muted-foreground text-center'>
              Validating Branch access token...
            </p>
            <p className='text-xs text-muted-foreground text-center mt-2'>
              Please wait while we verify your access permissions
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state or invalid token
  if (error || !tokenValidation?.valid) {
    const errorMessage =
      error?.message ||
      tokenValidation?.message ||
      'The provided Branch token is not valid or has expired. Please check your link and try again.';

    return (
      <div className='min-h-screen flex items-center justify-center p-4'>
        <Card className='w-full max-w-md'>
          <CardHeader className='text-center'>
            <div className='flex justify-center mb-4'>
              <ShieldX className='h-12 w-12 text-destructive' />
            </div>
            <CardTitle className='text-xl'>Access Denied</CardTitle>
            <CardDescription>
              Your Branch access token is invalid or has expired
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>

            {tokenValidation?.expires_at && (
              <div className='p-3 bg-muted rounded-lg'>
                <p className='text-xs text-muted-foreground'>
                  <strong>Token Expiry:</strong>{' '}
                  {new Date(tokenValidation.expires_at).toLocaleString()}
                </p>
              </div>
            )}

            <div className='flex flex-col gap-2'>
              <Button
                variant='outline'
                onClick={() => refetch()}
                className='w-full'
              >
                Try Again
              </Button>
              <Button
                variant='ghost'
                onClick={() => window.close()}
                className='w-full'
              >
                Close Window
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid token - render children
  return <>{children}</>;
}
